import { useState, type JSX, type ReactNode } from 'react'

import {
  RX_TYPES,
  binocularPd,
  dioptre,
  formatDioptre,
  type JobStatus,
  type OrderStatus,
  type PrescriptionStatus,
  type RxType,
  branchDate,
  toNumber,
  transpose,
} from '@lensmira/domain'

import { DEFAULT_LOCALE, formatDay, translate, type Params } from '@lensmira/i18n'

import { formatWireForDisplay, parseMinorUnits } from '../money.js'
import { Ltr, useTranslation, type Translation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { ContactLensSection, type ContactLensPrescriptionView } from './ContactLensSection.js'
import { RxComparison } from './RxComparison.js'
import { jobStatusLabel, orderStatusLabel, rxStatusLabel, rxTypeLabel } from '../status-labels.js'

/**
 * The customer profile. §6.2 `CUS-002`; `RX-002`; `PK-023`; `RX-006`, `RX-007`.
 *
 * `CUS-002`: "A customer holds: prescription history (all versions), purchase history, job
 * history, fitting measurements per frame owned, payment/credit balance, recall schedule, and
 * consent records." All six sections are here, and none of them is a fetch: this screen makes no
 * writes and has no read of its own, so `OFF-001a` does not apply and it takes the whole thing as
 * one prop. Whoever navigates here already resolved the customer; assembling the six sections into
 * one object is the parent's job, same as `JobDetailData` for `JobDetail`.
 *
 * **Prescriptions are grouped by `RxType`, not shown as one flat list.** A customer's distance
 * glasses and their contact lenses each carry their own `version` sequence starting at 1
 * (`RX-002`), so a global sort by version number alone would interleave two unrelated histories
 * under numbers that only mean something inside their own chain.
 *
 * **"Current" is `status === 'active'`, deliberately not "the head of the chain".** Those are two
 * different facts and this screen states both, never conflating them: `supersededBy === undefined`
 * only says nothing has replaced a version, and Appendix C.4's own transition table gives `expired`
 * no edge to `superseded`, so a lapsed prescription is permanently the head of its chain with
 * nothing ever marked over it, and still not the one a dispenser should fill a lens order against.
 * Marking that one "current" would be the wrong kind of obvious. When no version in a group is
 * `active`, the screen says so in as many words rather than silently showing nothing, because a
 * customer whose Rx has simply lapsed is the ordinary case, not the edge case.
 *
 * **Every version states both supersede facts, every time**, per RX-002's "a correction creates a
 * new version with a supersede link, never an edit": what it replaced (with the reason, which
 * `RX-002` requires) and what replaced it, or that nothing has. Each is a real link
 * (`#rx-version-{id}`) to the referenced version's own card when that version is in the list
 * handed to this screen, with `tabIndex={-1}` on the target so the jump actually moves focus for a
 * keyboard user (`KB-02`) rather than only scrolling the page under it. A reference outside the
 * given list reads as a plain sentence rather than a dead link.
 *
 * **The balance is one signed figure, in minor units as a decimal string, never split into two
 * fields that could disagree.** `PK-023`: "a running customer balance... **without euphemism**".
 * Positive means the customer owes the shop (the udhaar case this line exists for); negative means
 * the shop holds credit for the customer. The sign is read with `bigint` comparison, never a
 * string scan of a leading `-` and never `Number()` on it (`CLAUDE.md`), and the words are "Owes"
 * and "Credit of", not "Outstanding", which is the euphemism `PK-023` is written against.
 *
 * **Purchase history and job history are one list, jobs nested under the order that raised them.**
 * `CUS-002` names them as two bullets, but nothing in the PRD suggests a job exists independent of
 * the order it belongs to, and `OrderList.tsx` already established this exact shape for the same
 * data read a different way (the counter's work queue). Reusing its markup and class names here,
 * not just its shape in prose, means one CSS ruleset serves both screens instead of two that drift.
 * See `openQuestions` if a flat, order-independent job list turns out to be what was meant.
 *
 * **Fitting measurements reuse `packages/domain`'s own `binocularPd`** rather than adding the two
 * monocular halves here, per `RX-007`: "Binocular PD is derived and displayed", not stored, and
 * not re-derived a second time in a UI file.
 *
 * **Recall schedule and consent records have no backing type in `packages/domain` yet.** Nothing in
 * Appendix E enumerates a recall reason or a consent purpose, so both are shown as the shop's own
 * words (`reason`, `label`), typed as plain strings rather than invented enums. See
 * `openQuestions`.
 */

// -------------------------------------------------------------------------------------------
// Data shapes. Matching what a customer profile endpoint would read, not imported from
// `packages/domain`'s `Prescription` or `FittingMeasurement` directly: those carry fields (ids,
// database-shaped eyes) this screen does not need and lack ones it does (a frame's shop-facing
// label, a prescriber's name as free text). `JobDetailData` and `OrderSummary` set this precedent
// already: a screen's own view shape, built from the domain's enums and formatters, not its
// records.
// -------------------------------------------------------------------------------------------

export interface PrescriptionEyeView {
  /** Minus-cylinder, which is this product's canon. `RX-003`. */
  readonly sphere: number
  readonly cylinder: number
  readonly axis?: number
  /**
   * What was typed, where that was plus-cylinder. Absent means it was typed in the canonical form,
   * which is the ordinary case, not that it is unknown.
   *
   * Shown rather than recomputed, because `RX-003` says transposition is "never destructive" and a
   * toggle that derives the entered form from the canonical one is a second opinion about what
   * somebody typed rather than a record of it.
   */
  readonly enteredSphere?: number
  readonly enteredCylinder?: number
  readonly enteredAxis?: number
  readonly add?: number
  /** `RXW-006`: the rest of what the record stores. Absent means not recorded. */
  readonly intermediateAdd?: number
  readonly prismHorizontal?: number
  readonly prismBaseHorizontal?: string
  readonly prismVertical?: number
  readonly prismBaseVertical?: string
  readonly prismOblique?: number
  readonly prismObliqueDegrees?: number
  readonly backVertexDistanceMm?: number
  readonly isBalanceLens?: boolean
}

/**
 * One eye as the chosen notation shows it. `RX-003`, the display half.
 *
 * > "Transposition is a first-class operation, **displayed on demand**, never destructive."
 *
 * Minus-cylinder is what is stored, so the minus view is the row itself. The plus view prefers the
 * entered columns where they exist, because those are what somebody typed off a hospital slip, and
 * falls back to the arithmetic where they do not. `transpose` is its own inverse, which is what
 * makes offering this as a toggle safe.
 *
 * A spherical eye transposes to itself and has no axis to rotate, which `transpose` handles; a
 * cylinder with no axis cannot be transposed at all and Appendix I would not have stored one, so
 * the guard returns the row rather than throwing in a render.
 */
function shown(eye: PrescriptionEyeView, plus: boolean): PrescriptionEyeView {
  if (!plus) return eye
  if (eye.enteredSphere !== undefined && eye.enteredCylinder !== undefined) {
    return {
      ...eye,
      sphere: eye.enteredSphere,
      cylinder: eye.enteredCylinder,
      ...(eye.enteredAxis === undefined ? {} : { axis: eye.enteredAxis }),
    }
  }
  if (eye.cylinder === 0) return eye
  if (eye.axis === undefined) return eye
  const t = transpose({ sphere: dioptre(eye.sphere), cylinder: dioptre(eye.cylinder), axis: eye.axis })
  return {
    ...eye,
    sphere: toNumber(t.sphere),
    cylinder: toNumber(t.cylinder),
    ...(t.axis === undefined ? {} : { axis: t.axis }),
  }
}

/** Whether either eye was typed in plus-cylinder, which is worth saying in the minus view. */
function wasEnteredInPlus(rx: PrescriptionVersionView): boolean {
  return rx.od.enteredSphere !== undefined || rx.os.enteredSphere !== undefined
}

export interface PrescriptionVersionView {
  readonly id: string
  readonly version: number
  readonly type: RxType
  readonly status: PrescriptionStatus
  /** ISO `yyyy-mm-dd`. */
  readonly issueDate: string
  readonly expiryDate?: string
  readonly prescriberName?: string
  /** `RX-002`: the id of the version this one replaced, if any. */
  readonly supersedes?: string
  /** `RX-002`: the id of the version that replaced this one, if any. */
  readonly supersededBy?: string
  readonly supersedeReason?: string
  readonly od: PrescriptionEyeView
  readonly os: PrescriptionEyeView
}

export interface CustomerJobView {
  readonly id: string
  readonly status: JobStatus
  /** Calendar date, `YYYY-MM-DD`. Absent when the lab has not yet committed to one (`JOB-004`). */
  readonly promisedOn?: string
}

export interface CustomerOrderView {
  readonly id: string
  /**
   * The invoice number, or null until `TAX-002` issues one.
   *
   * Null rather than a placeholder: an English string in a data field cannot be translated and
   * leaks into any sentence that interpolates it. The label is this app's, which is what
   * `orders-list.service.ts` asks for in words.
   */
  readonly number: string | null
  readonly status: OrderStatus
  /** ISO 4217, shared by both amounts below. */
  readonly currency: string
  /** Minor units, as a string. Never parsed into a number in this file. */
  readonly totalMinor: string
  readonly outstandingMinor: string
  readonly branchName: string
  readonly staffName: string
  readonly jobs: readonly CustomerJobView[]
}

export interface FittingMeasurementView {
  /** `CAT-015`: sent with the lens list query so cut-out is checked at the point of choice. */
  readonly id: string
  readonly frameVariantId: string
  /** Shop-facing label for the frame, e.g. "Ray-Ban RB2140 · Black · 52mm". `packages/domain`'s
   *  `FittingMeasurement` carries only the id (`RX-006`); no catalogue lookup exists at this
   *  layer, so the parent supplies the label it already resolved. */
  readonly frameLabel: string
  readonly monocularPdOd: number
  readonly monocularPdOs: number
  readonly nearPd?: number
  readonly segHeightOd?: number
  readonly segHeightOs?: number
  readonly recordedOn?: string
}

export interface RecallView {
  readonly id: string
  /** e.g. "Annual eye test", "Contact lens check-up". Free text: see the header comment on why
   *  there is no registered enum for this yet. */
  readonly reason: string
  /** ISO `yyyy-mm-dd`. */
  readonly dueOn: string
}

export interface ConsentRecordView {
  readonly id: string
  /** e.g. "Marketing · WhatsApp", "Data processing". Free text; see the header comment. */
  readonly label: string
  readonly granted: boolean
  readonly recordedOn?: string
}

export interface CustomerBalanceView {
  readonly currency: string
  /** `PK-023`. Minor units, signed, as a decimal string. Positive: the customer owes the shop.
   *  Negative: the shop holds credit for the customer. Never two fields that could disagree. */
  readonly balanceMinor: string
}

export interface CustomerDetailData {
  readonly id: string
  readonly name: string
  readonly phone?: string
  readonly balance: CustomerBalanceView
  /**
   * Absent for a caller Appendix B denies `Prescription - view`, and §8.1 is why: a prescription is
   * special-category health data, so an accountant chasing a balance is not shown one.
   *
   * Optional rather than an empty array, because the two say different things. Empty means "this
   * person has no prescriptions"; absent means "you were not shown them". The section below says
   * which, so a dispenser does not go looking for a record that is there.
   */
  readonly prescriptions?: readonly PrescriptionVersionView[]
  /** `RX-001`, §5.2: absent when withheld with `prescriptions`, for the same reason. */
  readonly contactLensPrescriptions?: readonly ContactLensPrescriptionView[]
  readonly orders: readonly CustomerOrderView[]
  readonly fittings: readonly FittingMeasurementView[]
  /**
   * `RCL-001`'s recalls, absent until M5 builds them.
   *
   * Optional rather than an empty array from the read, because there is no recall table: an empty
   * array would say "no recalls are scheduled for this person", and the truth is that the product
   * cannot schedule one yet. The section says which.
   */
  readonly recalls?: readonly RecallView[]
  readonly consents: readonly ConsentRecordView[]
}

export interface CustomerDetailProps {
  readonly customer: CustomerDetailData
  /**
   * The branch's timezone, for `LOC-004`'s "locale-correct date format".
   *
   * Every date on this screen arrived as an ISO day and was printed as one: `2026-02-11` on the
   * prescription, the expiry, the promised date, the measurements and the consents. A shopkeeper
   * in Lahore does not write a date that way, and `LOC-004` is a `MUST`. Appendix J is why it is
   * the branch's zone rather than the device's: the day a record belongs to is the shop's day.
   */
  readonly timezone: string
  /**
   * Ask for a prescription copy for this version. `RXW-003`, Appendix L.
   *
   * A callback rather than the document, because the sheet is a separate read of six tables and
   * nobody wants it on every customer open. Absent means the shell has no configuration for it,
   * which is the same shape `loadStaff` uses, and the button is then not offered.
   */
  readonly onPrescriptionCopy?: (prescriptionId: string) => void
  /**
   * Per-version actions, `RX-002`: issue a draft, correct an active version. A slot rather than
   * callbacks because the actions are writes and this screen makes none (`OFF-001a`); the shell
   * supplies a component that owns the queue.
   */
  readonly renderPrescriptionActions?: (rx: PrescriptionVersionView) => ReactNode
  /** The same slot for a contact lens prescription: issue a draft, correct an active version. */
  readonly renderContactLensActions?: (rx: ContactLensPrescriptionView) => ReactNode
  /**
   * `RES-001`'s holds panel, injected rather than built here, like the two slots above.
   *
   * This screen is a view over a read: it owns no loader and takes no queue. The holds panel needs
   * both, plus the frame picker's source and the branch it is scoped to, which `App` already has.
   */
  readonly renderHolds?: () => ReactNode
}

// -------------------------------------------------------------------------------------------
// Vocabulary.
// -------------------------------------------------------------------------------------------

type OrderChipTone = 'neutral' | 'warn' | 'danger' | 'void'

/** Same table `OrderList.tsx` uses, kept in step deliberately: one order in two screens should
 *  never read as two different levels of urgency. See that file's header comment for why the four
 *  tones are not Appendix C.2's job-lifecycle palette. */
const ORDER_STATUS_TONE: Readonly<Record<OrderStatus, OrderChipTone>> = {
  draft: 'neutral',
  quoted: 'neutral',
  confirmed: 'neutral',
  partially_paid: 'neutral',
  paid: 'neutral',
  fulfilled: 'neutral',
  closed: 'neutral',
  on_hold: 'warn',
  refunded: 'danger',
  expired: 'void',
  cancelled: 'void',
}

type JobChipBucket =
  'draft' | 'ordered' | 'lab' | 'transit' | 'check' | 'ready' | 'done' | 'hold' | 'remake' | 'void'

/** Appendix C.2 read onto `DESIGN-TOKENS.md` §4.2's ten job-lifecycle chip colours, same mapping
 *  `OrderList.tsx` uses. */
const JOB_STATUS_CHIP: Readonly<Record<JobStatus, JobChipBucket>> = {
  draft: 'draft',
  ordered: 'ordered',
  sent: 'lab',
  acknowledged: 'lab',
  in_production: 'lab',
  chasing: 'hold',
  shipped: 'transit',
  received: 'transit',
  qc: 'check',
  qc_failed: 'remake',
  remake_raised: 'remake',
  ready: 'ready',
  collected: 'done',
  on_hold: 'hold',
  cancelled: 'void',
}

const JOB_STATUSES_NEVER_OVERDUE: ReadonlySet<JobStatus> = new Set(['collected', 'cancelled'])

type T = Translation['t']

// -------------------------------------------------------------------------------------------
// Numbers and dates. Dioptres through the domain's own formatter; money as bigint minor units,
// never a float, never `Number()` on a wire string (`CLAUDE.md`); dates compared as ISO strings,
// never through `Date`, for the reason `OrderList.tsx` states in its own header comment.
// -------------------------------------------------------------------------------------------

/** `RX-03`: the sign is always rendered. Falls back to the raw value on data that crossed the
 *  network and turned out not to be a clean 0.25 step, rather than throwing on a display screen. */
function formatPower(value: number): string {
  try {
    return formatDioptre(dioptre(value))
  } catch {
    return String(value)
  }
}

function formatOptionalPower(t: T, value: number | undefined): string {
  return value === undefined ? t('common.notRecorded') : formatPower(value)
}

/**
 * A minor-unit wire string to what a shopkeeper reads: thousands-grouped, currency code in front.
 * The same shape `OrderList.tsx`'s `formatOrderAmount` builds, kept as its own copy here rather
 * than imported: `apps/counter`'s screens do not import from one another (no screen here does),
 * and duplicating eight lines on top of `../money.ts`'s shared, exact parse is cheaper than
 * inventing a cross-screen module for one function.
 */
function formatCustomerAmount(minor: string, currency: string): string {
  return formatWireForDisplay(minor, currency)
}

type BalanceKey =
  'customerDetail.balance.none' | 'customerDetail.balance.owes' | 'customerDetail.balance.credit'

/**
 * `PK-023`, plainly. The sign is read with `bigint` comparison against `0n`, never a string scan
 * for a leading `-` (which also mishandles `"-0"`) and never `Number()` on the wire value.
 */
export function formatBalance(
  balance: CustomerBalanceView,
  t: (key: BalanceKey, params?: Params) => string = (key, params) =>
    translate(DEFAULT_LOCALE, key, params),
): string {
  const minor = parseMinorUnits(balance.balanceMinor)
  if (minor === 0n) return t('customerDetail.balance.none')
  const magnitude = minor < 0n ? -minor : minor
  const amount = formatCustomerAmount(magnitude.toString(), balance.currency)
  return t(minor > 0n ? 'customerDetail.balance.owes' : 'customerDetail.balance.credit', { amount })
}

/*
 * **Today is the branch's day, not the device's.** §16.3; `LOC-004`; Appendix J.
 *
 * This read the tablet's own clock, which is right only while the tablet agrees with the branch.
 * An owner looking at a branch in another zone, or a tablet whose timezone was never set, judged
 * "overdue" against the wrong day: a job promised for today showed the red flag, or one a day late
 * did not. `branchDate` is the same function the ledger uses to decide which day a sale belongs
 * to, so the queue and the books agree about when a day ends.
 */

function isJobOverdue(job: CustomerJobView, todayIso: string): boolean {
  if (job.promisedOn === undefined) return false
  if (JOB_STATUSES_NEVER_OVERDUE.has(job.status)) return false
  return job.promisedOn < todayIso
}

function isRecallOverdue(recall: RecallView, todayIso: string): boolean {
  return recall.dueOn < todayIso
}

// -------------------------------------------------------------------------------------------
// Prescription grouping. RX-002: each RxType is its own version chain.
// -------------------------------------------------------------------------------------------

interface PrescriptionGroup {
  readonly type: RxType
  /** Newest version first. */
  readonly versions: readonly PrescriptionVersionView[]
  /** The `active` version in this chain, if any. See the header comment on why this is not the
   *  same fact as "the highest version number". */
  readonly current: PrescriptionVersionView | undefined
  readonly mostRecent: PrescriptionVersionView
}

function groupPrescriptions(
  prescriptions: readonly PrescriptionVersionView[],
): readonly PrescriptionGroup[] {
  const byType = new Map<RxType, PrescriptionVersionView[]>()
  for (const rx of prescriptions) {
    const existing = byType.get(rx.type)
    if (existing) existing.push(rx)
    else byType.set(rx.type, [rx])
  }

  const groups: PrescriptionGroup[] = []
  for (const type of RX_TYPES) {
    const versions = byType.get(type)
    if (versions === undefined || versions.length === 0) continue
    const sorted = [...versions].sort((a, b) => b.version - a.version)
    const mostRecent = sorted[0]
    if (mostRecent === undefined) continue // Unreachable: `sorted.length > 0` was just checked.
    const current = sorted.find((v) => v.status === 'active')
    groups.push({ type, versions: sorted, current, mostRecent })
  }
  return groups
}

/** `RX-002`'s supersede link, read backwards: what this version replaced, and why. */
function supersedesLine(
  t: T,
  rx: PrescriptionVersionView,
  byId: ReadonlyMap<string, PrescriptionVersionView>,
): JSX.Element {
  if (rx.supersedes === undefined) {
    return <p className="lm-hint">{t('customerDetail.rx.firstVersion')}</p>
  }
  const prior = byId.get(rx.supersedes)
  const reason = rx.supersedeReason
  if (prior === undefined) {
    return (
      <p className="lm-hint">
        {reason !== undefined
          ? t('customerDetail.rx.supersedesHiddenReason', { reason })
          : t('customerDetail.rx.supersedesHidden')}
      </p>
    )
  }
  const link = (
    <a href={`#rx-version-${prior.id}`}>
      {t('customerDetail.rx.versionRef', { version: prior.version })}
    </a>
  )
  return (
    <p className="lm-hint">
      {reason !== undefined
        ? sentence(t, 'customerDetail.rx.supersedesLinkReason', { link, reason })
        : sentence(t, 'customerDetail.rx.supersedesLink', { link })}
    </p>
  )
}

/**
 * `RXW-006`: this version against the one it names in `supersedes`, not "version minus one", since
 * the distance and near chains keep separate numbering. Nothing when the prior is not in the list;
 * `supersedesLine` already says so.
 */
function comparisonOf(
  t: T,
  rx: PrescriptionVersionView,
  byId: ReadonlyMap<string, PrescriptionVersionView>,
): JSX.Element | null {
  const prior = rx.supersedes === undefined ? undefined : byId.get(rx.supersedes)
  if (prior === undefined) return null
  return (
    <details className="lm-rx-compare">
      <summary>{t('customerDetail.rx.compare', { version: prior.version })}</summary>
      <RxComparison previous={prior} current={rx} />
    </details>
  )
}

/** `RX-002`'s supersede link, read forwards: what replaced this version, or that nothing has. */
function supersededByLine(
  t: T,
  rx: PrescriptionVersionView,
  byId: ReadonlyMap<string, PrescriptionVersionView>,
): JSX.Element {
  if (rx.supersededBy === undefined) {
    return <p className="lm-hint">{t('customerDetail.rx.notSuperseded')}</p>
  }
  const next = byId.get(rx.supersededBy)
  if (next === undefined) {
    return <p className="lm-hint">{t('customerDetail.rx.supersededHidden')}</p>
  }
  const link = (
    <a href={`#rx-version-${next.id}`}>
      {t('customerDetail.rx.versionRef', { version: next.version })}
    </a>
  )
  return <p className="lm-hint">{sentence(t, 'customerDetail.rx.supersededByLink', { link })}</p>
}

/** Four sentences rather than three joined, because the clauses do not keep their order or their
 *  commas in every language. Dates stay left to right inside whichever one is chosen. */
function issuedLine(t: T, rx: PrescriptionVersionView, day: (iso: string) => string): JSX.Element {
  const issued = <Ltr>{day(rx.issueDate)}</Ltr>
  const expires = rx.expiryDate === undefined ? undefined : <Ltr>{day(rx.expiryDate)}</Ltr>
  const prescriber = rx.prescriberName
  if (expires !== undefined && prescriber !== undefined) {
    return sentence(t, 'customerDetail.rx.issuedExpiresBy', { issued, expires, prescriber })
  }
  if (expires !== undefined)
    return sentence(t, 'customerDetail.rx.issuedExpires', { issued, expires })
  if (prescriber !== undefined)
    return sentence(t, 'customerDetail.rx.issuedBy', { issued, prescriber })
  return sentence(t, 'customerDetail.rx.issued', { issued })
}

// -------------------------------------------------------------------------------------------
// The screen.
// -------------------------------------------------------------------------------------------

export function CustomerDetail({
  customer,
  timezone,
  onPrescriptionCopy,
  renderPrescriptionActions,
  renderContactLensActions,
  renderHolds,
}: CustomerDetailProps): JSX.Element {
  const { t, locale } = useTranslation()
  /**
   * `RX-003`'s "displayed on demand", and one piece of state for the whole list rather than one per
   * prescription: somebody checking a hospital slip against what is on file wants every version in
   * the same notation, and a per-row toggle makes two rows disagree about which notation they are
   * showing with nothing on screen saying so.
   */
  const [plusCylinder, setPlusCylinder] = useState(false)
  // `7 Oct 2026` in both locales. `formatDay` rather than a local parse, because the dates on this
  // screen are `date` columns and an instant pushed through a zone moves a calendar date: see its
  // own docstring for the New York case.
  const day = (value: string): string => formatDay(value, timezone, locale)
  const rxTypeLabelOf = (type: RxType): string => rxTypeLabel(t, type)
  const today = branchDate(Date.now(), timezone)
  const withheldRx = customer.prescriptions === undefined
  const prescriptions = customer.prescriptions ?? []
  const prescriptionGroups = groupPrescriptions(prescriptions)
  const versionsById = new Map(prescriptions.map((rx) => [rx.id, rx] as const))

  return (
    <section className="lm-screen" aria-labelledby="customer-detail-heading">
      <h1 id="customer-detail-heading" className="lm-screen__heading">
        {customer.name}
      </h1>
      {customer.phone !== undefined ? (
        <p className="lm-hint" dir="ltr">
          {customer.phone}
        </p>
      ) : null}

      <div className="lm-customer-balance">
        <h2 className="lm-screen__subheading">{t('customerDetail.balance.heading')}</h2>
        <p className="lm-customer-balance__amount" dir="ltr" role="status">
          {formatBalance(customer.balance, t)}
        </p>
      </div>

      <div>
        <h2 className="lm-screen__subheading">{t('customerDetail.rx.heading')}</h2>
        <p className="lm-hint">{t('customerDetail.rx.keptNote')}</p>

        {withheldRx ? (
          <p className="lm-hint">{t('customerDetail.rx.withheld')}</p>
        ) : prescriptionGroups.length === 0 ? (
          <p className="lm-hint">{t('customerDetail.rx.none')}</p>
        ) : (
          prescriptionGroups.map((group) => (
            <div key={group.type}>
              <h3>{rxTypeLabelOf(group.type)}</h3>

              {group.current === undefined ? (
                <p className="lm-hint" role="status">
                  {t('customerDetail.rx.noneInForce', {
                    typeLabel: rxTypeLabelOf(group.type).toLowerCase(),
                    version: group.mostRecent.version,
                    status: rxStatusLabel(t, group.mostRecent.status).toLowerCase(),
                  })}
                </p>
              ) : null}

              <ul className="lm-rx-history">
                {group.versions.map((rx) => (
                  <li key={rx.id}>
                    <article
                      id={`rx-version-${rx.id}`}
                      tabIndex={-1}
                      className={
                        rx.status === 'active'
                          ? 'lm-rx-version lm-rx-version--current'
                          : 'lm-rx-version'
                      }
                      aria-labelledby={`rx-version-title-${rx.id}`}
                    >
                      <div className="lm-screen__actions">
                        <span id={`rx-version-title-${rx.id}`}>
                          {t('customerDetail.rx.versionHeading', { version: rx.version })}
                        </span>
                        <span
                          className={
                            rx.status === 'active' ? 'lm-badge lm-badge--open' : 'lm-badge'
                          }
                        >
                          {rxStatusLabel(t, rx.status)}
                        </span>
                        {/* `RXW-003`: the sheet a customer takes to another dispenser. Offered on
                            every version they are entitled to, which is every status except draft
                            and discarded, and those never appear in this list. */}
                        {onPrescriptionCopy === undefined ? null : (
                          <button
                            type="button"
                            className="lm-button lm-button--secondary"
                            onClick={() => {
                              onPrescriptionCopy(rx.id)
                            }}
                          >
                            {t('rxCopy.open')}
                          </button>
                        )}
                      </div>
                      {renderPrescriptionActions?.(rx)}

                      <table dir="ltr">
                        <caption className="lm-visually-hidden">
                          {t('customerDetail.rx.tableCaption', {
                            typeLabel: rxTypeLabelOf(group.type),
                            version: rx.version,
                          })}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">{t('rx.col.eye')}</th>
                            <th scope="col">{t('rx.col.sphere')}</th>
                            <th scope="col">{t('rx.col.cylinder')}</th>
                            <th scope="col">{t('rx.col.axis')}</th>
                            <th scope="col">{t('rx.col.add')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(
                            [
                              ['customerDetail.rx.row.right', shown(rx.od, plusCylinder)],
                              ['customerDetail.rx.row.left', shown(rx.os, plusCylinder)],
                            ] as const
                          ).map(([label, eye]) => (
                            <tr key={label}>
                              <th scope="row">{t(label)}</th>
                              <td>{formatPower(eye.sphere)}</td>
                              <td>{formatPower(eye.cylinder)}</td>
                              <td>{eye.axis === undefined ? t('common.notRecorded') : eye.axis}</td>
                              <td>{formatOptionalPower(t, eye.add)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* `RX-003`: shown on demand, and the record says which notation it is in
                          rather than leaving a reader to work it out from the signs. */}
                      <p className="lm-rx-notation">
                        <span>
                          {t(plusCylinder ? 'rx.notation.plus' : 'rx.notation.minus')}
                        </span>
                        <button
                          type="button"
                          className="lm-button"
                          onClick={() => {
                            setPlusCylinder(!plusCylinder)
                          }}
                        >
                          {t(plusCylinder ? 'rx.notation.showMinus' : 'rx.notation.showPlus')}
                        </button>
                        {!plusCylinder && wasEnteredInPlus(rx) ? (
                          <span className="lm-hint">{t('rx.notation.enteredInPlus')}</span>
                        ) : null}
                      </p>

                      <p className="lm-hint">{issuedLine(t, rx, day)}</p>

                      {supersedesLine(t, rx, versionsById)}
                      {comparisonOf(t, rx, versionsById)}
                      {supersededByLine(t, rx, versionsById)}
                    </article>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>

      <ContactLensSection
        versions={customer.contactLensPrescriptions}
        timezone={timezone}
        {...(renderContactLensActions === undefined
          ? {}
          : { renderActions: renderContactLensActions })}
      />

      {/* `RES-001`. Under the prescriptions, because a frame is held after the eyes are known. */}
      {renderHolds?.()}

      <div>
        <h2 className="lm-screen__subheading">{t('customerDetail.orders.heading')}</h2>
        {customer.orders.length === 0 ? (
          <p className="lm-hint">{t('customerDetail.orders.none')}</p>
        ) : (
          <ul className="lm-order-list">
            {customer.orders.map((order) => {
              const overdueJobCount = order.jobs.filter((job) => isJobOverdue(job, today)).length
              const orderClassName =
                overdueJobCount > 0 ? 'lm-order-card lm-order-card--overdue' : 'lm-order-card'
              const outstandingClassName =
                order.outstandingMinor === '0'
                  ? 'lm-order-card__amount-value'
                  : 'lm-order-card__amount-value lm-order-card__amount-value--owed'

              return (
                <li key={order.id}>
                  {/* Named by the number and the status: this list is already one customer's, so
                      the customer adds nothing, and two un-invoiced orders of theirs would
                      otherwise answer with the same name. */}
                  <article
                    className={orderClassName}
                    aria-labelledby={`order-number-${order.id} order-state-${order.id}`}
                  >
                    <div className="lm-order-card__header">
                      {/* `TAX-002` issues the number partway through, so a draft has none. The
                          wire used to answer with the English `(not yet invoiced)`; a label takes
                          the page's direction and a number does not. */}
                      <span
                        id={`order-number-${order.id}`}
                        className="lm-order-card__number"
                        {...(order.number === null ? {} : { dir: 'ltr' as const })}
                      >
                        {order.number ?? t('order.notInvoiced')}
                      </span>
                      <span
                        id={`order-state-${order.id}`}
                        className="lm-chip lm-chip--order"
                        data-tone={ORDER_STATUS_TONE[order.status]}
                      >
                        {orderStatusLabel(t, order.status)}
                      </span>
                    </div>

                    <dl className="lm-order-card__totals">
                      <div className="lm-order-card__amount">
                        <dt>{t('common.total')}</dt>
                        <dd dir="ltr" className="lm-order-card__amount-value">
                          {formatCustomerAmount(order.totalMinor, order.currency)}
                        </dd>
                      </div>
                      <div className="lm-order-card__amount">
                        <dt>{t('common.outstanding')}</dt>
                        <dd dir="ltr" className={outstandingClassName}>
                          {formatCustomerAmount(order.outstandingMinor, order.currency)}
                        </dd>
                      </div>
                    </dl>

                    <p className="lm-order-card__attribution">
                      {order.branchName} · {order.staffName}
                    </p>

                    {order.jobs.length > 0 ? (
                      <ul className="lm-job-list">
                        {order.jobs.map((job) => {
                          const overdue = isJobOverdue(job, today)
                          const rowClassName = overdue
                            ? 'lm-job-row lm-job-row--overdue'
                            : 'lm-job-row'
                          return (
                            <li key={job.id} className={rowClassName}>
                              <span
                                className="lm-chip lm-chip--job"
                                data-chip={JOB_STATUS_CHIP[job.status]}
                              >
                                {jobStatusLabel(t, job.status)}
                              </span>
                              {job.promisedOn !== undefined ? (
                                <span className="lm-job-row__promised">
                                  {sentence(t, 'common.promised', {
                                    date: <Ltr>{day(job.promisedOn)}</Ltr>,
                                  })}
                                </span>
                              ) : null}
                              {overdue ? (
                                <span className="lm-job-row__overdue-flag">
                                  {t('common.overdue')}
                                </span>
                              ) : null}
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </article>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div>
        <h2 className="lm-screen__subheading">{t('customerDetail.fitting.heading')}</h2>
        <p className="lm-hint">{t('customerDetail.fitting.keptNote')}</p>
        {customer.fittings.length === 0 ? (
          <p className="lm-hint">{t('customerDetail.fitting.none')}</p>
        ) : (
          <ul className="lm-fitting-list">
            {customer.fittings.map((fitting) => (
              <li key={fitting.frameVariantId}>
                <article
                  className="lm-fitting-card"
                  aria-labelledby={`fitting-${fitting.frameVariantId}`}
                >
                  <p id={`fitting-${fitting.frameVariantId}`}>{fitting.frameLabel}</p>
                  <dl dir="ltr">
                    <div>
                      <dt>{t('customerDetail.fitting.monocularPd')}</dt>
                      <dd>
                        {t('customerDetail.fitting.pairMm', {
                          od: fitting.monocularPdOd,
                          os: fitting.monocularPdOs,
                        })}
                      </dd>
                    </div>
                    <div>
                      <dt>{t('customerDetail.fitting.binocularPd')}</dt>
                      {/* RX-007: derived and displayed, never re-stored. */}
                      <dd>
                        {t('customerDetail.fitting.valueMm', { value: binocularPd(fitting) })}
                      </dd>
                    </div>
                    {fitting.nearPd !== undefined ? (
                      <div>
                        <dt>{t('customerDetail.fitting.nearPd')}</dt>
                        <dd>{t('customerDetail.fitting.valueMm', { value: fitting.nearPd })}</dd>
                      </div>
                    ) : null}
                    {fitting.segHeightOd !== undefined || fitting.segHeightOs !== undefined ? (
                      <div>
                        <dt>{t('customerDetail.fitting.segmentHeight')}</dt>
                        <dd>
                          {t('customerDetail.fitting.pairMm', {
                            od: fitting.segHeightOd ?? t('common.notRecorded'),
                            os: fitting.segHeightOs ?? t('common.notRecorded'),
                          })}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {fitting.recordedOn !== undefined ? (
                    <p className="lm-hint">
                      {sentence(t, 'customerDetail.fitting.measuredOn', {
                        recordedOn: <Ltr>{day(fitting.recordedOn)}</Ltr>,
                      })}
                    </p>
                  ) : null}
                </article>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h2 className="lm-screen__subheading">{t('customerDetail.recall.heading')}</h2>
        {customer.recalls === undefined ? (
          <p className="lm-hint">{t('customerDetail.recall.notBuilt')}</p>
        ) : customer.recalls.length === 0 ? (
          <p className="lm-hint">{t('customerDetail.recall.none')}</p>
        ) : (
          <ul className="lm-recall-list">
            {customer.recalls.map((recall) => {
              const overdue = isRecallOverdue(recall, today)
              return (
                <li
                  key={recall.id}
                  className="lm-recall-row"
                  data-tone={overdue ? 'overdue' : 'upcoming'}
                >
                  <span>{recall.reason}</span>
                  <span>
                    {sentence(t, 'customerDetail.recall.due', { dueOn: <Ltr>{day(recall.dueOn)}</Ltr> })}
                  </span>
                  {overdue ? <span className="lm-badge">{t('common.overdue')}</span> : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div>
        <h2 className="lm-screen__subheading">{t('customerDetail.consent.heading')}</h2>
        {customer.consents.length === 0 ? (
          <p className="lm-hint">{t('customerDetail.consent.none')}</p>
        ) : (
          <ul className="lm-consent-list">
            {customer.consents.map((consent) => (
              <li key={consent.id} className="lm-consent-row">
                <span>{consent.label}</span>
                <span className={consent.granted ? 'lm-badge lm-badge--open' : 'lm-badge'}>
                  {consent.granted
                    ? t('customerDetail.consent.granted')
                    : t('customerDetail.consent.declined')}
                </span>
                {consent.recordedOn !== undefined ? (
                  <span dir="ltr">{day(consent.recordedOn)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
